import type {
  AutomationPreviewOwnershipRecovery,
  AutomationRunRecoveryView,
} from './automation-run-recovery'
import { z } from 'zod'

import {
  automationPreviewOwnershipRecoverySchema,
  automationRunRecoveryViewSchema,
} from './automation-run-recovery'

const lifecycleErrorBodySchema = z.object({
  message: z.string().optional(),
  error: z.object({
    message: z.string().optional(),
    issues: z.array(z.object({
      code: z.string().optional(),
      message: z.string().optional(),
    })).optional(),
  }).optional(),
  recoveryView: automationRunRecoveryViewSchema.optional(),
  ownershipRecovery: automationPreviewOwnershipRecoverySchema.optional(),
})

export class LiveOddsLifecycleApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code: string | null,
    readonly recoveryView: AutomationRunRecoveryView | null,
    readonly ownershipRecovery: AutomationPreviewOwnershipRecovery | null = null,
  ) {
    super(message)
    this.name = 'LiveOddsLifecycleApiError'
  }
}

export function requiresNewLiveOddsPreview(error: unknown): error is LiveOddsLifecycleApiError {
  return error instanceof LiveOddsLifecycleApiError
    && error.status === 409
    && error.recoveryView?.requiresNewPreview === true
    && error.recoveryView.status === 'invalidated'
}

export async function parseLiveOddsLifecycleJson<T>(response: Response): Promise<T> {
  if (response.ok) {
    return response.json() as Promise<T>
  }
  const fallback = `Request failed with status ${response.status}`
  try {
    const parsed = lifecycleErrorBodySchema.safeParse(await response.json())
    if (parsed.success) {
      const issue = parsed.data.error?.issues?.[0]
      throw new LiveOddsLifecycleApiError(
        parsed.data.message ?? parsed.data.error?.message ?? issue?.message ?? fallback,
        response.status,
        issue?.code ?? null,
        parsed.data.recoveryView ?? null,
        parsed.data.ownershipRecovery ?? null,
      )
    }
  }
  catch (error) {
    if (error instanceof LiveOddsLifecycleApiError) {
      throw error
    }
  }
  throw new LiveOddsLifecycleApiError(fallback, response.status, null, null, null)
}
